/**
 * Support for a preview tooltip combined with a draggable popup on click.
 */

import {Grow, Tooltip, tooltipClasses, type TooltipProps} from '@mui/material';
import * as Colors from '@wandb/weave/common/css/color.styles';
import React, {
  Children,
  type ComponentProps,
  useCallback,
  useRef,
  useState,
} from 'react';
import Draggable from 'react-draggable';
import styled from 'styled-components';

export const Popped = styled.div`
  border-radius: 4px;
  word-wrap: break-word;
  background-color: #fff;
  color: ${Colors.MOON_700};
  border: 1px solid ${Colors.MOON_300};
`;
Popped.displayName = 'S.Popped';

export const PoppedBody = styled.div`
  width: 600px;
  max-height: 50vh;
  overflow: auto;
`;
PoppedBody.displayName = 'S.PoppedBody';

export const StyledTooltip = styled(
  ({className, padding, ...props}: TooltipProps & {padding?: number}) => (
    <Tooltip {...props} classes={{popper: className}} />
  )
)(({theme, padding}) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#fff',
    color: Colors.MOON_700,
    border: `1px solid ${Colors.MOON_300}`,
    maxWidth: 600,
    maxHeight: '50vh',
    overflow: 'auto',
    padding,
  },
}));

export const TooltipHint = styled.div`
  color: ${Colors.MOON_500};
  text-align: center;
  font-size: 0.8em;
`;
TooltipHint.displayName = 'S.TooltipHint';

export function DraggableGrow({
  children,
  ref: growRef,
  ...growProps
}: ComponentProps<typeof Grow>) {
  const nodeRef = useRef<HTMLElement>(null);
  const child = Children.only(children);

  const setRefs = (node: HTMLElement | null) => {
    nodeRef.current = node;
    if (typeof growRef === 'function') {
      growRef(node);
    } else if (growRef && 'current' in growRef) {
      growRef.current = node;
    }
  };
  return (
    <Draggable nodeRef={nodeRef}>
      <Grow {...growProps} ref={setRefs}>
        {child}
      </Grow>
    </Draggable>
  );
}

type DraggableHandleProps = {
  children: React.ReactNode;
};

export const DraggableHandle = ({children}: DraggableHandleProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const onMouseDown = useCallback(() => setIsDragging(true), [setIsDragging]);
  const onMouseUp = useCallback(() => setIsDragging(false), [setIsDragging]);
  const style: React.CSSProperties = {
    cursor: isDragging ? 'grabbing' : 'grab',
  };

  return (
    <div
      className="handle"
      style={style}
      onMouseDown={onMouseDown}
      onMouseUp={onMouseUp}>
      {children}
    </div>
  );
};
